// FILE: src/components/FeedbackModal.js
//
// The learner-facing feedback submission form, opened from the global
// Feedback floating action button (FeedbackButton.js). Styling intentionally
// mirrors ReportModal.js so the two feel like the same family.
//
// ── Props ─────────────────────────────────────────────────────────────────────
//   onClose   {function} — dismiss (Cancel, ×, or Done after confirmation)
//   onSubmit  {function} — called with the assembled payload object; the modal
//                          stays open and transitions to a confirmation screen
//   defaults  {object}   — { route, pageName, certId, certLabel } auto-filled
//                          from the current app context. Page + certification
//                          are shown read-only; the learner cannot mistype them.
//
// Single-select inputs follow the project convention: Severity uses radio
// buttons; Category uses the established dropdown pattern from ReportModal.

import React, { useState } from 'react';
import { FEEDBACK_CATEGORIES, FEEDBACK_SEVERITIES } from '../../services/feedbackService';

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 2000, padding: '16px',
  },
  modal: {
    background: '#fff', borderRadius: '10px', border: '2px solid #333',
    width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid #eee', gap: '12px',
  },
  title: { fontWeight: 'bold', fontSize: '1.05rem', color: '#222', flex: 1 },
  close: {
    background: 'transparent', border: 'none', fontSize: '1.3rem',
    cursor: 'pointer', color: '#888', lineHeight: 1, padding: '2px 6px', borderRadius: '4px',
  },
  body: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  context: {
    background: '#f4f6fb', border: '1px solid #e5e8f1', borderRadius: '8px',
    padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px 18px',
    fontSize: '0.82rem', color: '#555',
  },
  contextItem: { display: 'flex', gap: '6px' },
  contextLabel: { color: '#8a90a2', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem', alignSelf: 'center' },
  contextValue: { color: '#2a2f45', fontWeight: 600 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '0.87rem', fontWeight: 600, color: '#444' },
  required: { color: '#c62828', marginLeft: '2px' },
  optional: { color: '#aaa', fontWeight: 'normal' },
  select: {
    padding: '9px 12px', fontSize: '0.95rem', border: '1px solid #ccc',
    borderRadius: '6px', background: '#fff', color: '#333', width: '100%', cursor: 'pointer',
  },
  textarea: {
    padding: '10px 12px', fontSize: '0.95rem', border: '1px solid #ccc',
    borderRadius: '6px', resize: 'vertical', minHeight: '96px', fontFamily: 'inherit',
    lineHeight: '1.5', color: '#333', width: '100%', boxSizing: 'border-box',
  },
  input: {
    padding: '9px 12px', fontSize: '0.95rem', border: '1px solid #ccc',
    borderRadius: '6px', width: '100%', boxSizing: 'border-box', color: '#333',
  },
  severityRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  severityChip: (active) => ({
    display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px',
    border: `1px solid ${active ? '#2c63ff' : '#ccc'}`, borderRadius: '8px',
    background: active ? '#eef2ff' : '#fff', color: active ? '#1a347d' : '#555',
    cursor: 'pointer', fontSize: '0.9rem', fontWeight: active ? 700 : 500,
  }),
  screenshotNote: {
    fontSize: '0.78rem', color: '#9097a8', fontStyle: 'italic',
    border: '1px dashed #d6dae6', borderRadius: '6px', padding: '8px 12px',
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
    padding: '14px 20px', borderTop: '1px solid #eee',
  },
  cancel: {
    padding: '10px 22px', background: 'transparent', border: '1px solid #bbb',
    borderRadius: '6px', cursor: 'pointer', fontSize: '0.95rem', color: '#555',
  },
  submit: (disabled) => ({
    padding: '10px 22px', background: disabled ? '#ccc' : '#2c63ff', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.95rem', fontWeight: 'bold', opacity: disabled ? 0.7 : 1,
  }),
  confirmation: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '40px 28px', textAlign: 'center', gap: '12px',
  },
  confirmIcon: { fontSize: '2.8rem', lineHeight: 1, color: '#2e7d32' },
  confirmHeading: { fontSize: '1.25rem', fontWeight: 'bold', color: '#222', margin: 0 },
  confirmBody: { fontSize: '0.93rem', color: '#555', lineHeight: '1.6', margin: 0 },
  done: {
    marginTop: '8px', padding: '10px 32px', background: '#333', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 'bold',
  },
};

export default function FeedbackModal({ onClose, onSubmit, defaults = {} }) {
  const [category, setCategory]   = useState('');
  const [severity, setSeverity]   = useState('Medium');
  const [description, setDesc]    = useState('');
  const [discord, setDiscord]     = useState('');
  const [submitted, setSubmitted] = useState(false);

  const pageName  = defaults.pageName || 'Unknown';
  const certLabel = defaults.certLabel || null;

  const canSubmit = category.length > 0 && description.trim().length > 0;

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit({
      category,
      severity,
      description,
      discordUsername: discord,
      route:           defaults.route || null,
      pageName:        defaults.pageName || null,
      certification:   defaults.certId || null,
      source:          'In App',
    });
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div data-testid="feedback-modal" style={styles.overlay}>
        <div style={styles.modal}>
          <div data-testid="feedback-confirmation" style={styles.confirmation}>
            <div style={styles.confirmIcon}>✓</div>
            <h3 style={styles.confirmHeading}>Thanks for the feedback!</h3>
            <p style={styles.confirmBody}>
              Your note has been logged for the team. If you left a Discord handle
              we may follow up there.
            </p>
            <button data-testid="feedback-done-button" style={styles.done} onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="feedback-modal" style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <span style={styles.title}>💬 Share Feedback</span>
          <button
            data-testid="feedback-close-button"
            style={styles.close}
            aria-label="Close feedback modal"
            onClick={onClose}
          >×</button>
        </div>

        <div style={styles.body}>
          {/* Auto-filled context — read-only */}
          <div style={styles.context}>
            <span style={styles.contextItem}>
              <span style={styles.contextLabel}>Page</span>
              <span style={styles.contextValue}>{pageName}</span>
            </span>
            {certLabel && (
              <span style={styles.contextItem}>
                <span style={styles.contextLabel}>Cert</span>
                <span style={styles.contextValue}>{certLabel}</span>
              </span>
            )}
          </div>

          {/* Category — required */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="feedback-category">
              Category <span style={styles.required}>*</span>
            </label>
            <select
              id="feedback-category"
              data-testid="feedback-category-select"
              style={styles.select}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Select a category…</option>
              {FEEDBACK_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Severity — single-select radio buttons */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Severity</label>
            <div style={styles.severityRow} role="radiogroup" aria-label="Severity">
              {FEEDBACK_SEVERITIES.map((s) => (
                <label key={s} style={styles.severityChip(severity === s)}>
                  <input
                    type="radio"
                    name="feedback-severity"
                    value={s}
                    checked={severity === s}
                    onChange={() => setSeverity(s)}
                    data-testid={`feedback-severity-${s.toLowerCase()}`}
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>

          {/* Description — required */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="feedback-description">
              Description <span style={styles.required}>*</span>
            </label>
            <textarea
              id="feedback-description"
              data-testid="feedback-description-input"
              style={styles.textarea}
              placeholder="Describe the issue, suggestion, or feedback."
              value={description}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>

          {/* Discord — optional */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="feedback-discord">
              Discord username <span style={styles.optional}>(optional — for follow-up)</span>
            </label>
            <input
              id="feedback-discord"
              data-testid="feedback-discord-input"
              type="text"
              style={styles.input}
              placeholder="e.g. pmpixel_fan#1234"
              value={discord}
              onChange={(e) => setDiscord(e.target.value)}
            />
          </div>

          {/* Screenshot — Phase 2 placeholder */}
          <div style={styles.screenshotNote}>
            📎 Screenshot attachments are coming soon.
          </div>
        </div>

        <div style={styles.footer}>
          <button data-testid="feedback-cancel-button" style={styles.cancel} onClick={onClose}>
            Cancel
          </button>
          <button
            data-testid="feedback-submit-button"
            style={styles.submit(!canSubmit)}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Submit Feedback
          </button>
        </div>
      </div>
    </div>
  );
}
